/**
 * API client with authentication support.
 *
 * Uses axios with interceptors to automatically attach the Keycloak
 * access token to all requests.
 *
 * Also transforms API responses from snake_case to camelCase.
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import Keycloak from 'keycloak-js'

/**
 * Convert snake_case string to camelCase.
 * Handles both letters and numbers after underscores.
 * IMPORTANT: Completely preserves keys starting with underscore.
 * These are internal/private fields (like _rowIndex, _delta_status, _originalRow)
 * that should not be transformed.
 */
function snakeToCamel(str: string): string {
  // Completely preserve keys starting with underscore (internal/private fields)
  // These come from the backend comparison service and frontend data processing
  if (str.startsWith('_')) {
    return str
  }

  // Convert the rest from snake_case to camelCase
  return str
    .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
    .replace(/_([0-9])/g, (_, digit) => digit) // Remove underscore before digits
}

/**
 * Keys whose values should NOT have their nested keys transformed.
 * These fields contain literal column names or feature keys as keys (data content),
 * not schema keys that should be converted to camelCase.
 */
/**
 * Keys whose values should NOT have their nested keys transformed at all.
 * These fields contain literal column names or feature keys as keys (data content),
 * not schema keys that should be converted to camelCase.
 */
const PRESERVE_NESTED_KEYS = new Set([
  'column_mappings',
  'columnMappings',
  'value_mappings',
  'valueMappings',
  'enabled_features',
  'enabledFeatures',
  'record_key',
  'recordKey',
])

/**
 * Keys that contain dict-of-objects where:
 * - The dictionary keys should be preserved (they're data like column names)
 * - But the nested objects' keys should still be transformed to camelCase
 * Example: { labels: { "USUBJID": { column_name: "..." } } }
 *   → The "USUBJID" key stays, but column_name becomes columnName
 */
const PRESERVE_DICT_KEYS_ONLY = new Set([
  'labels', // Column labels dict has column names as keys
])

// DEBUG: Flag to enable transform logging
let debugTransformLogged = false

/**
 * URL patterns for endpoints that return clinical data rows.
 * The `data` array in these responses contains row objects whose keys are
 * Parquet column names (data content) and must NOT be camelCase-transformed.
 */
const DATA_QUERY_URL_PATTERNS = [
  /\/data\/[^/]+\/[^/]+\/query/,      // POST /data/{studyId}/{domain}/query
]

/**
 * Check if a URL corresponds to a data query endpoint whose row data
 * should not have keys transformed.
 */
function isDataQueryUrl(url: string): boolean {
  return DATA_QUERY_URL_PATTERNS.some((pattern) => pattern.test(url))
}

/**
 * Transform a data query response: convert envelope keys (total, page_size, etc.)
 * to camelCase, but preserve the keys inside each row of the `data` array since
 * those are Parquet column names (data content, not API schema).
 */
function transformDataQueryResponse(obj: unknown): unknown {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return transformKeys(obj)
  }

  const transformed: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    const newKey = snakeToCamel(key)

    if ((key === 'data' || newKey === 'data') && Array.isArray(value)) {
      // Preserve row keys — these are Parquet column names
      // But still preserve underscore-prefixed metadata keys (_rowIndex, _delta_status, etc.)
      transformed[newKey] = value
    } else if ((key === 'compare_info' || newKey === 'compareInfo') && typeof value === 'object' && value !== null) {
      // Transform compareInfo envelope keys but preserve nested data content
      transformed[newKey] = transformKeys(value)
    } else {
      transformed[newKey] = transformKeys(value)
    }
  }
  return transformed
}

/**
 * Recursively transform object keys from snake_case to camelCase.
 * Preserves nested keys for fields that contain data content (like column mappings).
 */
function transformKeys(obj: unknown, preserveKeys = false): unknown {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeys(item, preserveKeys))
  }

  if (typeof obj === 'object' && obj !== null) {
    const transformed: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(obj)) {
      // Transform the key itself (unless we're in preserve mode)
      const newKey = preserveKeys ? key : snakeToCamel(key)

      // DEBUG: Log underscore-prefixed key transformations (once)
      if (key.startsWith('_') && !debugTransformLogged) {
        console.log('[transformKeys] Underscore key:', key, '→', newKey, '(should be same)')
        if (key !== newKey) {
          console.error('[transformKeys] BUG! Key was transformed:', key, '→', newKey)
        }
        debugTransformLogged = true
      }

      // Check if this key's value should have its nested keys preserved entirely
      const shouldPreserveNested = PRESERVE_NESTED_KEYS.has(key) || PRESERVE_NESTED_KEYS.has(newKey)

      // Check if this is a dict-of-objects where we preserve dict keys but transform nested object keys
      const shouldPreserveDictKeysOnly = PRESERVE_DICT_KEYS_ONLY.has(key) || PRESERVE_DICT_KEYS_ONLY.has(newKey)

      if (shouldPreserveNested && typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Preserve ALL nested keys as-is (don't transform column names or values)
        transformed[newKey] = value
      } else if (shouldPreserveDictKeysOnly && typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Preserve dict keys (e.g., column names like "USUBJID") but transform nested object keys
        const preservedDict: Record<string, unknown> = {}
        for (const [dictKey, dictValue] of Object.entries(value)) {
          // Keep the dict key as-is (it's a column name or similar data)
          // But transform the nested object's keys
          preservedDict[dictKey] = transformKeys(dictValue, false)
        }
        transformed[newKey] = preservedDict
      } else {
        transformed[newKey] = transformKeys(value, preserveKeys)
      }
    }
    return transformed
  }

  return obj
}

// Singleton Keycloak reference
let keycloakInstance: Keycloak | null = null

/**
 * Set the Keycloak instance for the API client to use.
 * This should be called after Keycloak is initialized.
 */
export function setKeycloakInstance(kc: Keycloak): void {
  keycloakInstance = kc
}

/**
 * Get the current access token, refreshing if needed.
 * Exported for use by other modules that need auth tokens (e.g., upload bypass).
 */
export async function getAccessToken(): Promise<string | null> {
  if (!keycloakInstance || !keycloakInstance.authenticated) {
    return null
  }

  try {
    // Refresh token if it expires in less than 30 seconds
    await keycloakInstance.updateToken(30)
    return keycloakInstance.token || null
  } catch (error) {
    console.error('Failed to refresh token:', error)
    return null
  }
}

/**
 * Create the axios instance with interceptors.
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: '/api/v1',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request interceptor to add auth token
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const token = await getAccessToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor for transforming snake_case to camelCase and error handling
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      // Skip transformation for blob responses (exports, file downloads)
      if (response.config.responseType === 'blob') {
        return response
      }

      // DEBUG: Log raw data for compare requests
      const url = response.config.url || ''
      if (url.includes('compare=true')) {
        console.log('[API Interceptor] BEFORE transform - compare request:', {
          url: url,
          dataLength: response.data?.data?.length,
          firstRowKeys: response.data?.data?.[0] ? Object.keys(response.data.data[0]).slice(-5) : [],
          hasRowIndex: response.data?.data?.[0]?._rowIndex !== undefined,
        })
      }

      // Transform response data from snake_case to camelCase
      // For data query endpoints, preserve row keys (Parquet column names)
      if (response.data) {
        if (isDataQueryUrl(url)) {
          response.data = transformDataQueryResponse(response.data)
        } else {
          response.data = transformKeys(response.data)
        }
      }

      // DEBUG: Log after transform for compare requests
      if (url.includes('compare=true')) {
        console.log('[API Interceptor] AFTER transform - compare request:', {
          dataLength: response.data?.data?.length,
          firstRowKeys: response.data?.data?.[0] ? Object.keys(response.data.data[0]).slice(-5) : [],
          hasRowIndex: response.data?.data?.[0]?._rowIndex !== undefined,
        })
      }

      return response
    },
    async (error) => {
      if (error.response?.status === 401) {
        // Token expired or invalid, try to refresh
        if (keycloakInstance) {
          try {
            await keycloakInstance.updateToken(0)
            // Retry the original request
            const originalRequest = error.config
            const token = keycloakInstance.token
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`
              return axios(originalRequest)
            }
          } catch {
            // Refresh failed, redirect to login
            keycloakInstance.login()
          }
        }
      }
      return Promise.reject(error)
    }
  )

  return client
}

// Export the API client instance
export const api = createApiClient()

// Convenience methods
export const apiGet = <T>(url: string, params?: Record<string, unknown>) =>
  api.get<T>(url, { params })

export const apiPost = <T>(url: string, data?: unknown) =>
  api.post<T>(url, data)

export const apiPut = <T>(url: string, data?: unknown) =>
  api.put<T>(url, data)

export const apiPatch = <T>(url: string, data?: unknown) =>
  api.patch<T>(url, data)

export const apiDelete = <T>(url: string) =>
  api.delete<T>(url)
